// Smart Contextual Prompts: Proactive vs Reactive pattern detection

export interface ContextualPrompt {
  id: string;
  type: 'time-pattern' | 'behavior-gap' | 'relational-pattern' | 'preventive';
  title: string;
  message: string;
  action: string;
  actionRoute: string;
  priority: 'high' | 'medium' | 'low';
  timestamp: number;
  dismissed: boolean;
  data?: any;
}

interface CheckIn {
  timestamp: string;
  rtiScore: number;
  components: {
    initiation: number;
    latency: number;
    emotionalDensity: number;
    availability: number;
    withdrawal: number;
  };
  relationshipId: string;
}

/**
 * Detect time-based patterns (e.g., "Sunday evening tension spikes")
 */
export function detectTimePatterns(checkins: CheckIn[]): ContextualPrompt[] {
  const prompts: ContextualPrompt[] = [];
  
  if (checkins.length < 7) {
    return prompts; // Need at least a week of data
  }

  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = now.getHours();

  // Analyze same day/time patterns
  const sameDayTimeCheckins = checkins.filter(checkin => {
    const checkinDate = new Date(checkin.timestamp);
    const checkinDay = checkinDate.getDay();
    const checkinHour = checkinDate.getHours();
    
    // Same day of week, within 2-hour window
    return checkinDay === dayOfWeek && Math.abs(checkinHour - hour) <= 2;
  });

  if (sameDayTimeCheckins.length >= 3) {
    const avgScore = sameDayTimeCheckins.reduce((sum, c) => sum + c.rtiScore, 0) / sameDayTimeCheckins.length;
    
    // High tension pattern detected
    if (avgScore > 60) {
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek];
      const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
      
      prompts.push({
        id: `time-pattern-${dayOfWeek}-${hour}`,
        type: 'time-pattern',
        title: 'Pattern Detected',
        message: `${dayName} ${timeOfDay} usually shows elevated tension (avg ${Math.round(avgScore)}). Here's a preventive action.`,
        action: 'Take Preventive Action',
        actionRoute: '/action',
        priority: 'high',
        timestamp: Date.now(),
        dismissed: false,
        data: { dayOfWeek, hour, avgScore }
      });
    }
  }

  // Weekend vs weekday patterns
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const weekendCheckins = checkins.filter(c => {
    const d = new Date(c.timestamp).getDay();
    return d === 0 || d === 6;
  });
  const weekdayCheckins = checkins.filter(c => {
    const d = new Date(c.timestamp).getDay();
    return d > 0 && d < 6;
  });

  if (weekendCheckins.length >= 4 && weekdayCheckins.length >= 4) {
    const weekendAvg = weekendCheckins.reduce((sum, c) => sum + c.rtiScore, 0) / weekendCheckins.length;
    const weekdayAvg = weekdayCheckins.reduce((sum, c) => sum + c.rtiScore, 0) / weekdayCheckins.length;
    
    const diff = Math.abs(weekendAvg - weekdayAvg);
    
    if (diff > 20) {
      const higherPeriod = weekendAvg > weekdayAvg ? 'weekends' : 'weekdays';
      const lowerPeriod = weekendAvg > weekdayAvg ? 'weekdays' : 'weekends';
      
      prompts.push({
        id: 'weekend-weekday-pattern',
        type: 'time-pattern',
        title: 'Weekly Pattern',
        message: `Tension is significantly higher during ${higherPeriod}. Plan for this during ${lowerPeriod}.`,
        action: 'See Pattern Details',
        actionRoute: '/insights',
        priority: 'medium',
        timestamp: Date.now(),
        dismissed: false,
        data: { weekendAvg, weekdayAvg, higherPeriod }
      });
    }
  }

  return prompts;
}

/**
 * Detect behavior gaps (e.g., "Haven't checked in for 3 days")
 */
export function detectBehaviorGaps(checkins: CheckIn[]): ContextualPrompt[] {
  const prompts: ContextualPrompt[] = [];

  if (checkins.length === 0) {
    return prompts;
  }

  // Check last check-in date
  const lastCheckin = checkins[checkins.length - 1];
  const lastCheckinDate = new Date(lastCheckin.timestamp);
  const now = new Date();
  const daysSinceLastCheckin = Math.floor((now.getTime() - lastCheckinDate.getTime()) / (1000 * 60 * 60 * 24));

  // Gap of 3+ days with high last RTI
  if (daysSinceLastCheckin >= 3 && lastCheckin.rtiScore >= 60) {
    prompts.push({
      id: `behavior-gap-${daysSinceLastCheckin}`,
      type: 'behavior-gap',
      title: 'Check-In Needed',
      message: `You haven't checked in for ${daysSinceLastCheckin} days and your last RTI was ${lastCheckin.rtiScore}. Quick pulse check?`,
      action: 'Check In Now',
      actionRoute: '/check-in',
      priority: 'high',
      timestamp: Date.now(),
      dismissed: false,
      data: { daysSinceLastCheckin, lastRTI: lastCheckin.rtiScore }
    });
  }

  // Gap of 5+ days (general reminder)
  if (daysSinceLastCheckin >= 5 && daysSinceLastCheckin < 7) {
    prompts.push({
      id: `behavior-gap-general-${daysSinceLastCheckin}`,
      type: 'behavior-gap',
      title: 'Awareness Break',
      message: `It's been ${daysSinceLastCheckin} days since your last check-in. Stay aware of your patterns.`,
      action: 'Resume Check-Ins',
      actionRoute: '/check-in',
      priority: 'medium',
      timestamp: Date.now(),
      dismissed: false,
      data: { daysSinceLastCheckin }
    });
  }

  // Week-long gap
  if (daysSinceLastCheckin >= 7) {
    prompts.push({
      id: 'behavior-gap-week',
      type: 'behavior-gap',
      title: 'Welcome Back',
      message: `You've been away for ${daysSinceLastCheckin} days. Let's get back on track with a quick check-in.`,
      action: 'Start Fresh',
      actionRoute: '/check-in',
      priority: 'high',
      timestamp: Date.now(),
      dismissed: false,
      data: { daysSinceLastCheckin }
    });
  }

  // Check for inconsistent check-in patterns
  if (checkins.length >= 14) {
    const last14Days = checkins.slice(-14);
    const checkInDays = new Set(last14Days.map(c => new Date(c.timestamp).toDateString()));
    const consistency = (checkInDays.size / 14) * 100;
    
    if (consistency < 50) {
      prompts.push({
        id: 'behavior-gap-consistency',
        type: 'behavior-gap',
        title: 'Consistency Matters',
        message: `You've checked in ${checkInDays.size} out of 14 days. Regular tracking reveals patterns.`,
        action: 'Build Habit',
        actionRoute: '/check-in',
        priority: 'low',
        timestamp: Date.now(),
        dismissed: false,
        data: { consistency, daysCheckedIn: checkInDays.size }
      });
    }
  }

  return prompts;
}

/**
 * Detect relational patterns (e.g., "Tension rises after 3+ days without quality time")
 */
export function detectRelationalPatterns(checkins: CheckIn[]): ContextualPrompt[] {
  const prompts: ContextualPrompt[] = [];

  if (checkins.length < 7) {
    return prompts;
  }

  // Analyze recent trend
  const recent5 = checkins.slice(-5);
  if (recent5.length === 5) {
    const isRising = recent5.every((checkin, i) => {
      if (i === 0) return true;
      return checkin.rtiScore >= recent5[i - 1].rtiScore - 5; // Allow 5-point variance
    });

    if (isRising && recent5[recent5.length - 1].rtiScore > 55) {
      const increase = recent5[recent5.length - 1].rtiScore - recent5[0].rtiScore;
      
      prompts.push({
        id: 'relational-pattern-rising',
        type: 'relational-pattern',
        title: 'Rising Tension Detected',
        message: `RTI has risen ${increase} points over 5 days. Consider reconnection actions.`,
        action: 'See Recommendations',
        actionRoute: '/action',
        priority: 'high',
        timestamp: Date.now(),
        dismissed: false,
        data: { increase, current: recent5[recent5.length - 1].rtiScore }
      });
    }
  }

  // Detect initiation imbalance pattern
  const recent7 = checkins.slice(-7);
  if (recent7.length === 7) {
    const avgInitiation = recent7.reduce((sum, c) => sum + (c.components.initiation || 0), 0) / 7;
    
    if (avgInitiation > 70) {
      prompts.push({
        id: 'relational-pattern-initiation',
        type: 'relational-pattern',
        title: 'Over-Pursuit Pattern',
        message: `You've been initiating heavily for 7 days (avg ${Math.round(avgInitiation)}%). Practice restraint.`,
        action: 'Learn Restraint',
        actionRoute: '/action',
        priority: 'high',
        timestamp: Date.now(),
        dismissed: false,
        data: { avgInitiation }
      });
    }
  }

  // Detect withdrawal pattern
  const avgWithdrawal = recent7.reduce((sum, c) => sum + (c.components.withdrawal || 0), 0) / recent7.length;
  if (avgWithdrawal > 65) {
    prompts.push({
      id: 'relational-pattern-withdrawal',
      type: 'relational-pattern',
      title: 'Withdrawal Signal',
      message: `Withdrawal signals detected for ${recent7.length} days. Small reconnection may help.`,
      action: 'Reconnect',
      actionRoute: '/action',
      priority: 'high',
      timestamp: Date.now(),
      dismissed: false,
      data: { avgWithdrawal }
    });
  }

  // Detect cyclical pattern (tension spikes every X days)
  if (checkins.length >= 21) {
    const spikes = checkins
      .map((c, i) => ({ index: i, score: c.rtiScore, date: new Date(c.timestamp) }))
      .filter(c => c.score > 65);

    if (spikes.length >= 3) {
      // Calculate average days between spikes
      const intervals: number[] = [];
      for (let i = 1; i < spikes.length; i++) {
        const daysBetween = Math.floor(
          (spikes[i].date.getTime() - spikes[i - 1].date.getTime()) / (1000 * 60 * 60 * 24)
        );
        intervals.push(daysBetween);
      }

      const avgInterval = intervals.reduce((sum, i) => sum + i, 0) / intervals.length;
      const variance = intervals.reduce((sum, i) => sum + Math.abs(i - avgInterval), 0) / intervals.length;

      // If intervals are consistent (low variance), we have a cycle
      if (variance < 3 && avgInterval >= 3) {
        const lastSpike = spikes[spikes.length - 1];
        const daysSinceLastSpike = Math.floor((Date.now() - lastSpike.date.getTime()) / (1000 * 60 * 60 * 24));
        const daysUntilNextSpike = Math.round(avgInterval - daysSinceLastSpike);

        if (daysUntilNextSpike >= 0 && daysUntilNextSpike <= 2) {
          prompts.push({
            id: 'relational-pattern-cycle',
            type: 'relational-pattern',
            title: 'Cyclical Pattern',
            message: `Tension typically spikes every ${Math.round(avgInterval)} days. Next spike predicted in ${daysUntilNextSpike} day${daysUntilNextSpike === 1 ? '' : 's'}.`,
            action: 'Prepare',
            actionRoute: '/insights',
            priority: 'medium',
            timestamp: Date.now(),
            dismissed: false,
            data: { avgInterval, daysUntilNextSpike }
          });
        }
      }
    }
  }

  return prompts;
}

/**
 * Generate all contextual prompts
 */
export function generateContextualPrompts(): ContextualPrompt[] {
  const checkinsData = localStorage.getItem('tension_checkins');
  if (!checkinsData) return [];

  const allCheckins: CheckIn[] = JSON.parse(checkinsData);
  const activeRelationshipId = localStorage.getItem('tension_active_relationship');
  
  // Filter for active relationship
  const checkins = activeRelationshipId
    ? allCheckins.filter(c => c.relationshipId === activeRelationshipId)
    : allCheckins;

  const timePatterns = detectTimePatterns(checkins);
  const behaviorGaps = detectBehaviorGaps(checkins);
  const relationalPatterns = detectRelationalPatterns(checkins);

  const allPrompts = [...timePatterns, ...behaviorGaps, ...relationalPatterns];

  // Filter out dismissed prompts
  const dismissedData = localStorage.getItem('tension_dismissed_prompts');
  const dismissed = dismissedData ? JSON.parse(dismissedData) : [];

  return allPrompts.filter(prompt => !dismissed.includes(prompt.id));
}

/**
 * Dismiss a prompt
 */
export function dismissPrompt(promptId: string) {
  const dismissedData = localStorage.getItem('tension_dismissed_prompts');
  const dismissed = dismissedData ? JSON.parse(dismissedData) : [];
  
  if (!dismissed.includes(promptId)) {
    dismissed.push(promptId);
    localStorage.setItem('tension_dismissed_prompts', JSON.stringify(dismissed));
  }
}

/**
 * Clear dismissed prompts (reset)
 */
export function clearDismissedPrompts() {
  localStorage.removeItem('tension_dismissed_prompts');
}

/**
 * Get prompt priority color
 */
export function getPromptColor(priority: 'high' | 'medium' | 'low') {
  switch (priority) {
    case 'high':
      return {
        border: 'border-red-200',
        bg: 'bg-red-50',
        text: 'text-red-900',
        icon: 'text-red-600',
        button: 'bg-red-600 hover:bg-red-700'
      };
    case 'medium':
      return {
        border: 'border-amber-200',
        bg: 'bg-amber-50',
        text: 'text-amber-900',
        icon: 'text-amber-600',
        button: 'bg-amber-600 hover:bg-amber-700'
      };
    case 'low':
      return {
        border: 'border-blue-200',
        bg: 'bg-blue-50',
        text: 'text-blue-900',
        icon: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      };
  }
}

/**
 * Schedule notification for contextual prompt
 */
export function schedulePromptNotification(prompt: ContextualPrompt) {
  const settingsData = localStorage.getItem('tension_notification_settings');
  const settings = settingsData ? JSON.parse(settingsData) : { actionReminders: true };

  if (!settings.actionReminders) {
    return;
  }

  // DEFENSIVE: Wrap all Notification usage in try-catch to prevent crashes
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('TENSION: ' + prompt.title, {
        body: prompt.message,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: prompt.id,
        requireInteraction: prompt.priority === 'high',
      });
    }
  } catch (error) {
    // Silently fail if Notification API is broken
    console.debug('Notification API not available:', error);
  }
}